import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, SchoolLevel, SubjectSlug } from '@jeraisonne/db';

/**
 * Bilan par chapitre de l'objectif Diagnostic.
 *
 * Chaque chapitre du niveau a (idéalement) un exercice diagnostic. À partir du
 * meilleur score obtenu (0-100, source `attempts.score` — cf. objective-progress),
 * on classe le chapitre en :
 *   - 'acquis'      : meilleur score moyen ≥ seuil
 *   - 'a_revoir'    : évalué mais < seuil
 *   - 'non_evalue'  : aucune tentative complétée
 */

/** Seuil d'acquisition, en % (sur 100). */
export const DIAGNOSTIC_ACQUIS_THRESHOLD = 70;

export type DiagnosticChapterStatus = 'acquis' | 'a_revoir' | 'non_evalue';

export interface DiagnosticExerciseRef {
  id: string;
  chapterId: string;
  chapterTitle: string;
  subject: SubjectSlug | null;
}

export interface DiagnosticChapterRow {
  chapterId: string;
  chapterTitle: string;
  subject: SubjectSlug | null;
  /** Tous les exercices diagnostic de ce chapitre (≥ 1). */
  exerciseIds: string[];
  /** Moyenne des meilleurs scores des exos évalués (0-100), ou null si aucun. */
  bestScore: number | null;
  status: DiagnosticChapterStatus;
}

/**
 * Pure : regroupe les exercices diagnostic par chapitre et calcule le statut
 * acquis/à revoir/non évalué. L'ordre des chapitres suit la 1re apparition.
 */
export function computeDiagnosticBreakdown(
  exercises: DiagnosticExerciseRef[],
  bestScoreByExercise: Map<string, number>,
  threshold: number = DIAGNOSTIC_ACQUIS_THRESHOLD,
): DiagnosticChapterRow[] {
  interface Acc {
    chapterId: string;
    chapterTitle: string;
    subject: SubjectSlug | null;
    exerciseIds: string[];
    scores: number[];
  }

  const order: string[] = [];
  const byChapter = new Map<string, Acc>();

  for (const ex of exercises) {
    let acc = byChapter.get(ex.chapterId);
    if (!acc) {
      acc = {
        chapterId: ex.chapterId,
        chapterTitle: ex.chapterTitle,
        subject: ex.subject,
        exerciseIds: [],
        scores: [],
      };
      byChapter.set(ex.chapterId, acc);
      order.push(ex.chapterId);
    }
    acc.exerciseIds.push(ex.id);
    const score = bestScoreByExercise.get(ex.id);
    if (typeof score === 'number') acc.scores.push(score);
  }

  return order.map((chapterId) => {
    const acc = byChapter.get(chapterId)!;
    if (acc.scores.length === 0) {
      return {
        chapterId: acc.chapterId,
        chapterTitle: acc.chapterTitle,
        subject: acc.subject,
        exerciseIds: acc.exerciseIds,
        bestScore: null,
        status: 'non_evalue' as const,
      };
    }
    const avg = Math.round(acc.scores.reduce((s, v) => s + v, 0) / acc.scores.length);
    return {
      chapterId: acc.chapterId,
      chapterTitle: acc.chapterTitle,
      subject: acc.subject,
      exerciseIds: acc.exerciseIds,
      bestScore: avg,
      status: avg >= threshold ? ('acquis' as const) : ('a_revoir' as const),
    };
  });
}

/**
 * Récupère les exercices diagnostic du niveau (avec chapitre) + le meilleur
 * score complété par exercice pour l'élève, puis calcule le bilan par chapitre.
 */
export async function fetchDiagnosticBreakdown(
  supabase: SupabaseClient<Database>,
  studentId: string,
  studentLevel: SchoolLevel,
): Promise<DiagnosticChapterRow[]> {
  const { data: exos } = await supabase
    .from('exercises')
    .select('id, chapter_id, chapters!inner ( title, subject, level )')
    .eq('status', 'published')
    // is_diagnostic est postérieur aux types stale → cast de la colonne.
    .eq('is_diagnostic' as 'is_fundamental', true)
    .eq('chapters.level', studentLevel);

  const refs: DiagnosticExerciseRef[] = (exos ?? []).map((e: any) => ({
    id: e.id as string,
    chapterId: e.chapter_id as string,
    chapterTitle: (e.chapters?.title ?? '(chapitre inconnu)') as string,
    subject: (e.chapters?.subject ?? null) as SubjectSlug | null,
  }));
  if (refs.length === 0) return [];

  const { data: attempts } = await supabase
    .from('attempts')
    .select('exercise_id, score')
    .eq('student_id', studentId)
    .eq('status', 'completed')
    .in(
      'exercise_id',
      refs.map((r) => r.id),
    );

  const best = new Map<string, number>();
  for (const a of attempts ?? []) {
    if (a.exercise_id === null) continue;
    const cur = best.get(a.exercise_id) ?? 0;
    if ((a.score ?? 0) > cur) best.set(a.exercise_id, a.score ?? 0);
  }

  return computeDiagnosticBreakdown(refs, best);
}
